import React, { useState, useEffect, useCallback, useRef } from 'react';
import useApi from '../hooks/useApi';
import { 
    Calendar, Loader2, RefreshCw, CheckCircle2, Clock, 
    AlertCircle, ArrowRight, User, Package, FileText, 
    DollarSign, Send, PlayCircle, FileCheck, Bug, Search, Filter, X
} from 'lucide-react';
import { io } from "socket.io-client";
import { getSocketUrl } from '../config'; 

// --- CONFIGURAÇÃO DE SOCKET ---
const SOCKET_URL = getSocketUrl(); 

// --- Componente de Busca de Cliente ---
const ClientSearchInput = ({ onSelect, initialValue = null }) => {
    const { apiFetch } = useApi();
    const [searchTerm, setSearchTerm] = useState(initialValue ? initialValue.name : '');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const wrapperRef = useRef(null);

    // Debounce manual
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchTerm && searchTerm !== initialValue?.name) {
                setLoading(true);
                apiFetch(`/general-clients?search=${searchTerm}`)
                    .then(data => {
                        const list = Array.isArray(data) ? data : (data.clients || []);
                        setResults(list);
                    })
                    .catch(err => {
                        console.error("Erro busca cliente:", err);
                        setResults([]);
                    })
                    .finally(() => setLoading(false));
            } else {
                setResults([]);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm, apiFetch, initialValue]);

    const handleSelect = (client) => {
        setSearchTerm(client.name);
        onSelect(client);
        setResults([]);
    };

    return (
        <div className="relative w-full" ref={wrapperRef}>
            <div className="relative">
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        if(e.target.value === '') onSelect(null); 
                    }}
                    placeholder="Buscar Cliente..."
                    className="w-full p-2 pl-8 border rounded-md text-sm"
                />
                <Search size={16} className="absolute left-2.5 top-2.5 text-gray-400" />
                {searchTerm && (
                    <button 
                        onClick={() => { setSearchTerm(''); onSelect(null); }}
                        className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                    >
                        <X size={16} />
                    </button>
                )}
            </div>
            {results.length > 0 && (
                <ul className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {results.map(c => (
                        <li 
                            key={c.id} 
                            onClick={() => handleSelect(c)}
                            className="p-2 hover:bg-gray-100 cursor-pointer text-sm"
                        >
                            <p className="font-medium">{c.name}</p>
                            {c.document_id && <p className="text-xs text-gray-500">{c.document_id}</p>}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

// --- Componentes Visuais ---
const ProgressBar = ({ percentage }) => {
    let colorClass = 'bg-blue-600';
    if (percentage === 100) colorClass = 'bg-green-500';
    else if (percentage >= 80) colorClass = 'bg-indigo-600';
    else if (percentage >= 50) colorClass = 'bg-yellow-500';
    else if (percentage > 0) colorClass = 'bg-orange-500';
    else colorClass = 'bg-gray-300';

    return (
        <div className="w-full bg-gray-200 rounded-full h-5 dark:bg-gray-700 overflow-hidden relative shadow-inner mt-2">
            <div 
                className={`${colorClass} h-5 rounded-full transition-all duration-1000 ease-out flex items-center justify-center relative overflow-hidden`} 
                style={{ width: `${percentage}%` }}
            >
                {percentage < 100 && percentage > 0 && (
                    <div className="absolute top-0 left-0 bottom-0 right-0 bg-white/20 animate-pulse"></div>
                )}
                {percentage >= 10 && (
                    <span className="text-[10px] font-bold text-white drop-shadow-md z-10">{percentage}%</span>
                )}
            </div>
        </div>
    );
};

const StageIcon = ({ percentage, isCompleted }) => {
    if (isCompleted) return <CheckCircle2 className="text-green-500" size={28} />;
    if (percentage === 0) return <Clock className="text-gray-400" size={28} />;
    if (percentage <= 20) return <PlayCircle className="text-orange-500" size={28} />;
    if (percentage <= 30) return <FileText className="text-yellow-600" size={28} />;
    if (percentage <= 50) return <Package className="text-blue-500" size={28} />;
    if (percentage <= 60) return <FileCheck className="text-cyan-600" size={28} />;
    if (percentage <= 80) return <DollarSign className="text-indigo-500" size={28} />;
    return <Send className="text-purple-500" size={28} />;
};

const ComercialStatusPage = ({ currentUser }) => {
    const { apiFetch } = useApi();
    
    // --- ESTADOS DOS FILTROS ---
    const [selectedDate, setSelectedDate] = useState(() => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    });
    const [selectedClient, setSelectedClient] = useState(null);
    const [statusFilter, setStatusFilter] = useState('todos');

    const [statusData, setStatusData] = useState([]);
    const [debugInfo, setDebugInfo] = useState(null); 
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [lastUpdated, setLastUpdated] = useState(new Date());

    const fetchData = useCallback(async () => {
        setLoading(true);
        setDebugInfo(null);
        
        try {
            const params = new URLSearchParams();
            if (selectedDate) params.append('date', selectedDate);
            if (selectedClient) params.append('clientId', selectedClient.id);
            if (statusFilter !== 'todos') params.append('status', statusFilter);

            console.log("Solicitando dados:", params.toString());
            
            const response = await apiFetch(`/comercial-status?${params.toString()}`);
            
            let results = [];
            if (response && response.results) {
                results = response.results;
                setDebugInfo(response.debug);
            } else if (Array.isArray(response)) {
                results = response;
            }

            setStatusData(results);
            setError('');
            setLastUpdated(new Date());

        } catch (err) {
            console.error("Erro:", err);
            setError('Falha na comunicação.');
            setDebugInfo({ error: err.message });
        } finally {
            setLoading(false);
        }
    }, [apiFetch, selectedDate, selectedClient, statusFilter]); 

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        const socket = io(SOCKET_URL);
        socket.on('connect', () => {
            if (currentUser?.entityIdentifier) {
                socket.emit('join_entity_room', `entity_${currentUser.entityIdentifier}`);
            }
        });
        socket.on('data_updated', () => fetchData());
        socket.on('status_updated', () => fetchData());
        return () => socket.disconnect();
    }, [currentUser, fetchData]);

    // Formatador de data auxiliar
    const formatJustDate = (dateString) => {
        if (!dateString) return null;
        try {
            const dateObj = new Date(dateString);
            if (isNaN(dateObj.getTime())) return dateString.split('T')[0];
            return dateObj.toLocaleDateString('pt-BR');
        } catch(e) {
            return dateString;
        }
    };

    // Totais
    const totalAtendimentos = statusData.length;
    const concluidos = statusData.filter(i => i.percentage === 100).length;
    const percentualGeral = totalAtendimentos > 0 ? Math.round((concluidos / totalAtendimentos) * 100) : 0;

    return (
        <div className="p-3 md:p-6 max-w-7xl mx-auto min-h-screen">
            {/* Cabeçalho */}
            <div className="mb-6 md:mb-8">
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-blue-600 rounded-lg text-white shadow-md"><Clock size={24} className="md:w-7 md:h-7" /></div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Status Geral</h1>
                </div>
                <p className="text-gray-500 ml-1 text-sm md:text-base">Acompanhamento operacional em tempo real.</p>
            </div>

            {/* --- BARRA DE FILTROS --- */}
            <div className="mb-6 p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                    <div className="md:col-span-3">
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Data</label>
                        <div className="relative">
                            <input 
                                type="date" 
                                value={selectedDate} 
                                onChange={(e) => setSelectedDate(e.target.value)} 
                                className="w-full p-2 pl-8 border rounded-md text-sm"
                            />
                            <Calendar size={16} className="absolute left-2.5 top-2.5 text-gray-400" />
                            {selectedDate && (
                                <button 
                                    onClick={() => setSelectedDate('')}
                                    className="absolute right-8 top-2.5 text-gray-400 hover:text-red-500"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="md:col-span-5">
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Cliente</label>
                        <ClientSearchInput onSelect={setSelectedClient} initialValue={selectedClient} />
                    </div>

                    <div className="md:col-span-3">
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Status</label>
                        <div className="relative">
                            <select 
                                value={statusFilter} 
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full p-2 pl-8 border rounded-md text-sm appearance-none bg-white"
                            >
                                <option value="todos">Todos</option>
                                <option value="aberto">Em Aberto (Qualquer etapa)</option>
                                <option value="concluido">Concluído Total (100%)</option>
                                <option value="0">Agendado (0%)</option>
                                <option value="10">Em Andamento (10%)</option>
                                <option value="20">Finalizado Operacional (20%)</option>
                                <option value="30">OS Aberta (30%)</option>
                                <option value="40">Serviços Definidos (40%)</option>
                                <option value="60">Laudos Prontos (60%)</option>
                                <option value="80">Fat. Liberado (80%)</option>
                                <option value="90">Faturado (90%)</option>
                            </select>
                            <Filter size={16} className="absolute left-2.5 top-2.5 text-gray-400" />
                        </div>
                    </div>

                    <div className="md:col-span-1">
                        <button 
                            onClick={fetchData} 
                            className="w-full p-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 flex justify-center items-center transition-colors"
                            title="Atualizar Lista"
                        >
                            <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Dashboard Resumo */}
            {!loading && !error && statusData.length > 0 && (
                <div className="grid grid-cols-3 gap-2 md:gap-4 mb-6">
                    <div className="bg-white p-2 md:p-4 rounded-lg shadow-sm border border-gray-100 flex flex-col md:flex-row items-center justify-between text-center md:text-left">
                        <div><p className="text-[10px] md:text-xs font-bold text-gray-400 uppercase">Total</p><p className="text-lg md:text-2xl font-bold text-gray-800">{totalAtendimentos}</p></div>
                        <div className="hidden md:block p-3 bg-gray-100 rounded-full text-gray-500"><User size={20}/></div>
                    </div>
                    <div className="bg-white p-2 md:p-4 rounded-lg shadow-sm border border-gray-100 flex flex-col md:flex-row items-center justify-between text-center md:text-left">
                        <div><p className="text-[10px] md:text-xs font-bold text-gray-400 uppercase">Feitos</p><p className="text-lg md:text-2xl font-bold text-green-600">{concluidos}</p></div>
                        <div className="hidden md:block p-3 bg-green-100 rounded-full text-green-600"><CheckCircle2 size={20}/></div>
                    </div>
                    <div className="bg-white p-2 md:p-4 rounded-lg shadow-sm border border-gray-100 flex flex-col md:flex-row items-center justify-between text-center md:text-left">
                        <div><p className="text-[10px] md:text-xs font-bold text-gray-400 uppercase">Efic.</p><p className="text-lg md:text-2xl font-bold text-blue-600">{percentualGeral}%</p></div>
                        <div className="hidden md:block p-3 bg-blue-100 rounded-full text-blue-600"><CheckCircle2 size={20}/></div>
                    </div>
                </div>
            )}

            {/* Loading e Erros */}
            {loading && statusData.length === 0 ? (
                <div className="flex flex-col justify-center items-center h-64">
                    <Loader2 className="animate-spin text-blue-600 mb-4" size={48} />
                    <p className="text-gray-500 animate-pulse">A procurar dados...</p>
                </div>
            ) : error ? (
                <div className="p-6 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-md flex items-center gap-3">
                    <AlertCircle size={24} />
                    <p>{error}</p>
                </div>
            ) : (
                <>
                    {statusData.length === 0 && (
                        <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-200 border-dashed mb-8">
                            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                <Search className="text-gray-400" size={32} />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-700">Nenhum resultado encontrado</h3>
                            <p className="text-gray-500 mt-2">Tente ajustar os filtros de data, cliente ou status.</p>
                        </div>
                    )}

                    {/* Lista de Cards */}
                    <div className="grid grid-cols-1 gap-4 md:gap-6">
                        {statusData.map((item) => {
                            const isCompleted = item.percentage === 100;
                            const cardBorderColor = isCompleted ? 'border-green-300' : 'border-gray-200';
                            const cardBgColor = isCompleted ? 'bg-gradient-to-r from-white to-green-50' : 'bg-white';
                            
                            return (
                                <div key={item.scheduleId} className={`relative overflow-hidden rounded-xl border ${cardBorderColor} ${cardBgColor} shadow-sm hover:shadow-lg transition-all duration-300 p-4 md:p-6`}>
                                    <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${isCompleted ? 'bg-green-500' : (item.percentage > 0 ? 'bg-blue-500' : 'bg-gray-300')}`}></div>
                                    <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-center">
                                        
                                        {/* Coluna Tempo e Icone */}
                                        <div className="flex flex-col items-center justify-center min-w-[80px] w-full md:w-auto border-b md:border-b-0 md:border-r border-gray-100 pb-4 md:pb-0 md:pr-6">
                                            <div className="text-3xl font-bold text-gray-700 tracking-tight">{item.time}</div>
                                            <div className="mt-2 md:mt-3 bg-white p-2 rounded-full shadow-sm border border-gray-100">
                                                <StageIcon percentage={item.percentage} isCompleted={isCompleted} />
                                            </div>
                                        </div>

                                        {/* Coluna Info Cliente */}
                                        <div className="flex-1 w-full text-center md:text-left md:border-l md:border-gray-100 md:pl-6">
                                            <h3 className="text-lg font-bold text-gray-900 leading-tight">{item.clientName}</h3>
                                            <p className="text-sm text-gray-500 font-medium uppercase tracking-wide mt-1">{item.serviceType}</p>
                                            <div className="flex flex-wrap gap-2 justify-center md:justify-start mt-3">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                                    Status: {item.status || "Pendente"}
                                                </span>
                                                {item.osNumber && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                        OS #{item.osNumber}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Coluna Progresso */}
                                        <div className="flex-1 w-full md:min-w-[320px] bg-gray-50/50 p-4 rounded-lg border border-gray-100">
                                            <div className="flex justify-between text-xs font-bold mb-1 uppercase tracking-wider text-gray-500">
                                                <span>Progresso</span>
                                                <span className={isCompleted ? 'text-green-600' : 'text-blue-600'}>{item.percentage}%</span>
                                            </div>
                                            <ProgressBar percentage={item.percentage} />
                                            <div className="flex justify-between items-end mt-4">
                                                <div className="flex flex-col text-left">
                                                    <span className="text-[10px] text-gray-400 uppercase font-bold mb-0.5">Etapa Atual</span>
                                                    <span className={`text-sm font-bold ${isCompleted ? 'text-green-700' : 'text-blue-700'}`}>{item.currentStageLabel}</span>
                                                    
                                                    {/* NOVA INFORMAÇÃO: DATA DE INÍCIO */}
                                                    {item.scheduledDate && (
                                                        <span className="text-[10px] text-gray-500 mt-1 flex items-center gap-1">
                                                            <Calendar size={10} /> Iniciado em: {formatJustDate(item.scheduledDate)}
                                                        </span>
                                                    )}
                                                </div>
                                                
                                                <div className="flex flex-col items-end text-right">
                                                    {!isCompleted && (
                                                        <>
                                                            <span className="text-[10px] text-gray-400 uppercase font-bold mb-0.5">Próximo</span>
                                                            <div className="flex items-center text-sm font-medium text-gray-600 animate-pulse">
                                                                {item.nextStepLabel} <ArrowRight size={14} className="ml-1" />
                                                            </div>
                                                        </>
                                                    )}
                                                    {isCompleted && item.completionDate && (
                                                        <>
                                                            <span className="text-[10px] text-gray-400 uppercase font-bold mb-0.5">Concluído em</span>
                                                            <span className="text-sm font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded">
                                                                {formatJustDate(item.completionDate)}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {debugInfo && debugInfo.error && (
                        <div className="mt-10 p-4 bg-slate-800 rounded-lg text-slate-300 font-mono text-xs overflow-x-auto border border-slate-700 shadow-lg">
                            <h4 className="text-red-400 font-bold text-sm mb-2 flex items-center gap-2">
                                <Bug size={16} /> ERRO DE SISTEMA DETECTADO
                            </h4>
                            <div className="space-y-1">
                                <p><span className="text-blue-400">Mensagem:</span> {debugInfo.error}</p>
                                <p><span className="text-blue-400">Tabela:</span> {debugInfo.usingTable || 'N/A'}</p>
                            </div>
                        </div>
                    )}
                    
                    <div className="mt-8 text-right text-xs text-gray-400 flex items-center justify-end gap-2 border-t pt-4">
                        <Clock size={12} />
                        <span>Última atualização: {lastUpdated.toLocaleTimeString()}</span>
                    </div>
                </>
            )}
        </div>
    );
};

export default ComercialStatusPage;